import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import "@/App.css";
import { Toaster, toast } from "sonner";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import About from "@/components/About";
import Experiences from "@/components/Experiences";
import Boat from "@/components/Boat";
import Packages from "@/components/Packages";
import Calendar from "@/components/Calendar";
import Included from "@/components/Included";
import Cancellation from "@/components/Cancellation";
import BookingForm from "@/components/BookingForm";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";
import PdfDocument from "@/components/PdfDocument";
import VagasAdmin from "@/components/VagasAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LandingApp() {
  const pdfRef = useRef(null);
  const [selectedPackage, setSelectedPackage] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedDays, setSelectedDays] = useState(null);
  const [selectedCabin, setSelectedCabin] = useState("");
  const [selectedDateKey, setSelectedDateKey] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [availabilityMap, setAvailabilityMap] = useState({});

  const loadAvailability = async () => {
    try {
      const res = await axios.get(`${API}/availability`);
      setAvailabilityMap(res.data || {});
    } catch (e) {
      // silent fail; falls back to example data
    }
  };

  // Fetch backend availability (overrides examples)
  useEffect(() => {
    loadAvailability();
  }, []);

  const handleReserve = (pkg) => {
    setSelectedPackage(pkg);
    setTimeout(() => {
      document.getElementById("reserva")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handlePickDate = (date, days, cabin, dateKey) => {
    setSelectedDate(date);
    setSelectedDays(days || null);
    setSelectedCabin(cabin || "");
    setSelectedDateKey(dateKey || "");
    setSelectedPackage("");
    toast.success(
      date
        ? cabin
          ? `Data: ${date} · Quarto: ${cabin}`
          : `Data selecionada: ${date}`
        : "Você pode reservar agora e escolher a data depois.",
      { duration: 2800 }
    );
    setTimeout(() => {
      document.getElementById("reserva")?.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  const handleDownloadPdf = async () => {
    if (downloading) return;
    setDownloading(true);
    toast.info("Gerando seu PDF...", { duration: 2000 });
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const element = pdfRef.current;
      if (!element) throw new Error("PDF element not ready");

      const opt = {
        margin: 0,
        filename: "Abrolhos-2026-2027-Apecatu.pdf",
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, allowTaint: false, backgroundColor: "#FFFFFF", logging: false },
        jsPDF: { unit: "px", format: [794, 1123], orientation: "portrait", hotfixes: ["px_scaling"] },
        pagebreak: { mode: ["css", "legacy"] },
      };
      await html2pdf().set(opt).from(element).save();
      toast.success("PDF baixado com sucesso!");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao gerar PDF. Tente novamente.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="App" data-testid="app-root">
      <Toaster position="top-right" theme="dark" richColors />
      <Nav onDownloadPdf={handleDownloadPdf} />
      <main>
        <Hero />
        <About />
        <Experiences />
        <Boat />
        <Packages onReserve={handleReserve} />
        <Calendar onPickDate={handlePickDate} availabilityMap={availabilityMap} />
        <Included />
        <Cancellation />
        <BookingForm
          selectedPackage={selectedPackage}
          setSelectedPackage={setSelectedPackage}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          selectedDays={selectedDays}
          setSelectedDays={setSelectedDays}
          selectedCabin={selectedCabin}
          setSelectedCabin={setSelectedCabin}
          selectedDateKey={selectedDateKey}
          onAvailabilityChanged={loadAvailability}
        />
      </main>
      <Footer />
      <FloatingButtons onDownloadPdf={handleDownloadPdf} downloading={downloading} />

      <div style={{ position: "fixed", left: "-10000px", top: 0, width: "794px", pointerEvents: "none" }} aria-hidden="true">
        <PdfDocument ref={pdfRef} />
      </div>
    </div>
  );
}

function App() {
  const [route, setRoute] = useState({ hash: window.location.hash, path: window.location.pathname });
  useEffect(() => {
    const onChange = () => setRoute({ hash: window.location.hash, path: window.location.pathname });
    window.addEventListener("hashchange", onChange);
    window.addEventListener("popstate", onChange);
    return () => {
      window.removeEventListener("hashchange", onChange);
      window.removeEventListener("popstate", onChange);
    };
  }, []);

  const isAdmin = route.hash === "#/vagas" || route.path === "/paineldm" || route.path === "/paineldm/";
  if (isAdmin) return <VagasAdmin />;
  return <LandingApp />;
}

export default App;